import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Countdown, CountdownTask, Member } from '@pacer/shared';
import * as api from '../api';
import { CheckToggle } from './CheckToggle';
import { CountdownEditor } from './CountdownEditor';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Avatar, Button, IconButton, cx } from './ui';
import { daysUntil, untilLabel } from '../lib/countdowns';
import { useZone } from '../lib/dates';
import { queryKeys } from '../state/queries';
import { useActiveMemberId } from '../state/activeMember';

/**
 * The click-into view for a pinned plan: the countdown, prep progress, notes, and the
 * checklist of everything that has to happen to get there — each item checkable and
 * assignable to a person.
 */
export function PlanDetail({ countdown, onClose }: { countdown: Countdown; onClose: () => void }) {
  const zone = useZone();
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');

  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: tasks = [] } = useQuery({
    queryKey: ['countdown-tasks', countdown.id],
    queryFn: () => api.getCountdownTasks(countdown.id),
  });

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['countdown-tasks', countdown.id] });
    await qc.invalidateQueries({ queryKey: queryKeys.countdowns });
  };

  const add = useMutation({
    mutationFn: (t: string) => api.addCountdownTask(countdown.id, t, activeId),
    onSuccess: async () => {
      setText('');
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (t: CountdownTask) => api.updateCountdownTask(t.id, { done: !t.done, doneBy: activeId }),
    onMutate: async (t) => {
      await qc.cancelQueries({ queryKey: ['countdown-tasks', countdown.id] });
      const prev = qc.getQueryData<CountdownTask[]>(['countdown-tasks', countdown.id]);
      qc.setQueryData<CountdownTask[]>(['countdown-tasks', countdown.id], (old) =>
        old?.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(['countdown-tasks', countdown.id], ctx.prev),
    onSettled: refresh,
  });
  const assign = useMutation({
    mutationFn: ({ id, memberId }: { id: string; memberId: string | null }) =>
      api.updateCountdownTask(id, { assignedMemberId: memberId }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteCountdownTask(id), onSuccess: refresh });

  const days = daysUntil(countdown.targetDate, zone);
  const total = tasks.length;
  const doneCount = tasks.filter((t) => t.done).length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const color = countdown.color ?? 'var(--color-accent)';

  const submit = (): void => {
    const t = text.trim();
    if (t) add.mutate(t);
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={days >= 0 ? `In ${untilLabel(days)}` : untilLabel(days)}
      title={countdown.title}
      size="lg"
      footer={
        <Button variant="secondary" icon="settings" className="ml-auto" onClick={() => setEditing(true)}>
          Edit plan
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Countdown + progress */}
        <div className="flex items-center gap-4 rounded-lg border border-border-subtle bg-surface-high/50 p-4">
          <span
            className="flex size-14 shrink-0 items-center justify-center rounded-full"
            style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}
          >
            <Icon name={(countdown.icon as IconName) ?? 'star'} size={26} strokeWidth={1.3} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="tabular font-mono text-[26px] leading-none font-medium text-primary">
              {days > 0 ? `${days} days` : days === 0 ? 'Today' : untilLabel(days)}
            </div>
            <div className="mt-1 font-mono text-[12px] text-muted">
              {DateTime.fromISO(countdown.targetDate, { zone }).toFormat('cccc, LLLL d, yyyy')}
            </div>
          </div>
          {total > 0 && (
            <div className="text-right">
              <div className="tabular font-mono text-[15px] text-secondary">
                {doneCount}/{total}
              </div>
              <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-border-subtle">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          )}
        </div>

        {countdown.notes && (
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-secondary">{countdown.notes}</p>
        )}

        {/* Checklist */}
        <div className="flex flex-col gap-2">
          <span className="eyebrow">To get ready</span>
          <div className="flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder="Add something to do…"
              enterKeyHint="done"
              autoCapitalize="sentences"
              className="touch flex-1 rounded-md border border-border bg-surface-high px-3 text-[16px] text-primary placeholder:text-muted focus:border-accent focus:outline-none"
            />
            <IconButton icon="plus" label="Add task" variant="primary" onClick={submit} disabled={!text.trim()} />
          </div>

          {tasks.length > 0 && (
            <ul className="overflow-hidden rounded-md border border-border-subtle">
              {tasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  members={members}
                  onToggle={() => toggle.mutate(t)}
                  onAssign={(memberId) => assign.mutate({ id: t.id, memberId })}
                  onDelete={() => remove.mutate(t.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing && (
        <CountdownEditor
          countdown={countdown}
          onClose={() => {
            setEditing(false);
            // The plan may have been deleted from the editor; close the detail if so.
            void qc.invalidateQueries({ queryKey: queryKeys.countdowns });
          }}
        />
      )}
    </Modal>
  );
}

function TaskRow({
  task,
  members,
  onToggle,
  onAssign,
  onDelete,
}: {
  task: CountdownTask;
  members: Member[];
  onToggle: () => void;
  onAssign: (memberId: string | null) => void;
  onDelete: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const who = members.find((m) => m.id === task.assignedMemberId);

  return (
    <li className="group flex items-center gap-2 border-b border-border-subtle px-2.5 transition-colors last:border-b-0 hover:bg-surface-high/40">
      <button onClick={onToggle} className="touch flex flex-1 items-center gap-3 py-1.5 text-left" aria-pressed={task.done}>
        <CheckToggle checked={task.done} size={20} color={who?.color ?? 'var(--color-accent)'} />
        <span className={cx('flex-1 text-[14px]', task.done ? 'text-muted line-through' : 'text-primary')}>{task.text}</span>
      </button>

      {/* Assignee — tap to pick who's handling it. */}
      <div className="relative">
        <button
          onClick={() => setMenu((v) => !v)}
          className="flex size-8 items-center justify-center rounded-full"
          aria-label={who ? `Assigned to ${who.name}` : 'Assign'}
        >
          {who ? (
            <Avatar name={who.name} color={who.color} photoUrl={who.avatarUrl} size={24} />
          ) : (
            <span className="flex size-6 items-center justify-center rounded-full border border-dashed border-border text-muted">
              <Icon name="plus" size={11} />
            </span>
          )}
        </button>
        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-md border border-border bg-surface-high shadow-[0_12px_30px_rgba(0,0,0,0.4)]">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    onAssign(m.id === task.assignedMemberId ? null : m.id);
                    setMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-surface"
                >
                  <Avatar name={m.name} color={m.color} photoUrl={m.avatarUrl} size={20} />
                  <span className="flex-1 truncate text-primary">{m.name}</span>
                  {m.id === task.assignedMemberId && <Icon name="check" size={12} className="text-accent" />}
                </button>
              ))}
              {task.assignedMemberId && (
                <button
                  onClick={() => {
                    onAssign(null);
                    setMenu(false);
                  }}
                  className="w-full border-t border-border-subtle px-3 py-2 text-left font-mono text-[12px] text-muted hover:bg-surface"
                >
                  Unassign
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <IconButton
        icon="trash"
        label="Delete task"
        size="sm"
        onClick={onDelete}
        className="text-muted opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      />
    </li>
  );
}
